import React, {
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { StarFilled, StarOutlined } from '@ant-design/icons';
import { Breadcrumb, Flex, message, theme as antTheme } from 'antd';

import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Breadcrumb as BreadcrumbType } from '@totem/types/breadcrumb';
import { SubtitleLink } from '@totem/types/route';
import { isNotNull } from '@totem/utilities/common';
import { USER_ENDPOINT } from '@totem/utilities/endpoints';

import { useRoute } from '../../RouteContext';

import './contentLayout.css';

export interface ContentLayoutProps {
  /**
   * Content to render in the main content section
   */
  children: ReactNode;
  /**
   * Main title of the page. If truthy, overrides the default route page
   * title.
   */
  pageTitle?: string | ReactNode;
  /**
   * Optional subtitle to render under the main page title
   * Most pages follow the pattern of Region | Building | X
   * If the prop value is an array of SubtitleLink this will be constructed
   * Else the given ReactNode will simply be rendered as is
   */
  pageSubtitle?: SubtitleLink[] | ReactNode;
  /**
   * Content to render on the right side of the header opposite of the
   * page title
   */
  contentRight?: ReactNode;
  /**
   * Content to render as the entire page header. If truthy, pageTitle
   * and contentRight props will be ignored.
   */
  pageHeader?: ReactNode;
  /**
   * Optional function to override default route breadcrumbs.
   * The function will pass default route breadcrumbs as an argument.
   */
  breadcrumbs?: (breadcrumbs: BreadcrumbType[]) => BreadcrumbType[];
  /**
   * Optional styles to be applied to the main content container
   */
  contentStyle?: React.CSSProperties;

  landingPageName?: string;
}

const getBreadcrumbItems = (breadcrumbs: BreadcrumbType[]) => {
  return breadcrumbs.map(({ label, link }) => {
    return {
      title: link ? <Link to={link}>{label}</Link> : label,
    };
  });
};

const renderBreadcrumbs = (breadcrumbs: BreadcrumbType[]) => {
  return breadcrumbs ? (
    <Breadcrumb
      style={{ margin: '16px 0 16px 30px' }}
      items={getBreadcrumbItems(breadcrumbs)}
    />
  ) : null;
};

const isSubtitleLinkArray = (
  subtitle: SubtitleLink[] | React.ReactNode,
): subtitle is SubtitleLink[] => {
  // @ts-ignore
  return Array.isArray(subtitle);
};

const renderSubtitle = (subtitle: SubtitleLink[] | ReactNode) => {
  return isSubtitleLinkArray(subtitle) ? (
    subtitle.map(({ label, link }, index) => (
      <React.Fragment key={index}>
        {!!index && <div styleName="subtitle separator"> | </div>}
        {link ? (
          <Link to={link}>
            <div styleName="subtitle">{label}</div>
          </Link>
        ) : (
          <div styleName="subtitle">{label}</div>
        )}
      </React.Fragment>
    ))
  ) : (
    <div styleName="subtitle">{subtitle}</div>
  );
};

const ContentLayout = ({
  children,
  pageTitle,
  pageSubtitle,
  contentRight,
  pageHeader,
  breadcrumbs,
  contentStyle,
  landingPageName,
}: ContentLayoutProps) => {
  const route = useRoute();
  const { token } = antTheme.useToken();
  const { userProfile, getApiToken, refetch } = useContext(UserProfileContext);
  const [isSending, setIsSending] = useState(false);
  const userLandingPageName = useMemo(() => {
    if (
      isNotNull(userProfile) &&
      isNotNull(userProfile.preferences) &&
      isNotNull(userProfile.preferences.homePage)
    ) {
      return userProfile.preferences.homePage;
    }

    return '';
  }, [userProfile]);

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column' as const,
      flex: 1,
    },
    header: {
      //display: 'flex',
      //flexDirection: 'row' as const,
      //justifyContent: 'flex-start',
      //alignItems: 'center',
      padding: '1rem 3rem',
    },
    titleContainer: {
      backgroundColor: token.colorBgContainer,
    },
    title: {
      //fontFamily: 'Roboto, Nirmala',
      color: token.colorTextHeading,
      marginBottom: '1rem',
    },
    subTitle: {
      //fontFamily: 'Roboto, Nirmala',
      color: token.colorTextSecondary,
    },
    componentContainer: {
      backgroundColor: token.colorBgLayout,
      display: 'flex',
      flexFlow: 'column nowrap',
      flex: 1,
      padding: '3rem',
    },
  };

  const sendLandingPagePreference = useCallback(
    async (pageName: string) => {
      if (isSending) {
        return;
      }

      fetch(`${USER_ENDPOINT}/preferences/homePage`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: pageName,
      })
        .then((response) => {
          if (response.status === 200) {
            refetch();
          } else {
            message.error('Home page update failed!');
          }
        })
        .then(() => {
          setIsSending(false);
        });

      setIsSending(true);
    },
    [isSending],
  );

  return (
    <div style={styles.container}>
      <Flex justify="flex-start" align={'center'}>
        <div>
          {renderBreadcrumbs(
            breadcrumbs ? breadcrumbs(route.breadcrumbs) : route.breadcrumbs,
          )}
        </div>
        <div style={{ marginLeft: '1rem' }}>
          {userLandingPageName === landingPageName && (
            <StarFilled
              title={'Remote landing page preference'}
              style={{ color: '#FFC107' }}
              onClick={() => {
                sendLandingPagePreference('-');
              }}
            />
          )}
          {isNotNull(landingPageName) &&
            landingPageName !== '' &&
            userLandingPageName !== landingPageName && (
              <StarOutlined
                title={'Set as preferred landing page '}
                style={{ color: '#BBBBBB' }}
                onClick={() => {
                  sendLandingPagePreference(landingPageName);
                }}
              />
            )}
        </div>
      </Flex>
      <div>
        {pageHeader || (
          <Flex
            justify={'space-between'}
            align={'center'}
            style={styles.header}
          >
            <div style={styles.titleContainer}>
              <div styleName={'title'}>{pageTitle || route.pageTitle}</div>
              {pageSubtitle && (
                <div styleName={'subtitle-container'} style={styles.subTitle}>
                  {renderSubtitle(pageSubtitle)}
                </div>
              )}
            </div>
            {contentRight}
          </Flex>
        )}
      </div>
      <div style={{ ...styles.componentContainer, ...contentStyle }}>
        {children}
      </div>
    </div>
  );
};

export default ContentLayout;
